import { Button, Tag, Typography } from "@suraasa/placebo-ui"
import { Masterclass } from "api/resources/masterclasses/types"
import clsx from "clsx"
import { context } from "global/Context/context"
import { CheckCircle, PasteClipboard } from "iconoir-react"
import { useContext } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { formatDate } from "utils/helpers"

import DEFAULT_MASTERCLASS_IMAGE from "../../assets/Placeholder/default-masterclass-thumbnail.svg"
import RegisterButton from "./RegisterButton"

const RegisterMasterclass = ({
  masterclass,
  refetchMasterclass,
}: {
  masterclass: Masterclass
  refetchMasterclass: () => void
}) => {
  const [searchParams] = useSearchParams()
  const isRegisteredQuery = searchParams.get("isRegistered") === "true"

  const isRegistered = masterclass.isRegistered || isRegisteredQuery

  const { profile } = useContext(context)

  if (!masterclass) return <></>

  return (
    <div>
      <div className="rounded-lg border border-onSurface-200 p-2">
        <div className="flex  flex-col justify-between gap-y-1 sm:flex-row md:gap-0">
          <div className="flex flex-col gap-1 sm:flex-row">
            <img
              alt="thumbnail"
              className="w-full max-w-[167px] rounded-lg"
              src={masterclass.thumbnail || DEFAULT_MASTERCLASS_IMAGE}
              onError={e => (e.currentTarget.src = DEFAULT_MASTERCLASS_IMAGE)}
            />
            <div className="flex flex-col justify-between sm:ml-3">
              <div>
                <Typography variant="preTitle" className="text-onSurface-500">
                  Masterclass
                </Typography>
                <Typography
                  className="first-letter-capitalize mt-1.5 sm:mt-1"
                  variant="title3"
                >
                  {masterclass.title}
                </Typography>
              </div>
              <div className="mt-0.5 flex gap-0.5 sm:mt-0">
                <Typography variant="strong">On</Typography>
                <Typography variant="body">
                  {formatDate(masterclass.startTime, "d MMMM yyyy, p")}
                </Typography>
              </div>
            </div>
          </div>
          <Tag color="success" size="sm" className="h-fit">
            Upcoming
          </Tag>
        </div>
      </div>

      {isRegistered ? (
        <div
          className={clsx(
            "mt-2 flex w-full flex-row items-center justify-between rounded-lg bg-highlight-50 px-2 py-1",
            { "bg-success-50": isRegistered }
          )}
        >
          <Typography
            className={clsx("flex items-center gap-1 text-highlight-600", {
              "text-success-600": isRegistered,
            })}
            variant="strong"
          >
            <CheckCircle
              className={clsx("size-2.5 shrink-0 text-highlight-600", {
                "text-success-600": isRegistered,
              })}
            />
            <span>You have reserved your spot for this masterclass.</span>
          </Typography>
          {masterclass.joiningUrl && (
            <Link to={masterclass.joiningUrl}>
              <Button size="sm" color="success">
                Join
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="mt-2 flex w-full flex-row items-center justify-between rounded-lg bg-highlight-50 px-2 py-1">
          <Typography
            className="flex items-center gap-1 text-highlight-600"
            variant="strong"
          >
            <PasteClipboard className="size-2.5 shrink-0" />
            <span>Reserve your spot for this masterclass.</span>
          </Typography>
          <RegisterButton
            profile={profile}
            slug={masterclass.slug}
            onSuccess={() => {
              refetchMasterclass()
            }}
          />
        </div>
      )}
    </div>
  )
}

export default RegisterMasterclass
